if (!customElements.get('primo-collection-scroll')) {
  customElements.define('primo-collection-scroll', class extends HTMLElement {
    connectedCallback() {
      this.grid = this.closest('#ProductGridContainer')?.querySelector('#product-grid');
      this.status = this.querySelector('[role="status"]');
      this.link = this.querySelector('a');
      if (!this.grid || !this.link) return;
      this.paused = false;
      this.events = new AbortController();
      this.link.addEventListener('click', event => {
        event.preventDefault();
        this.load();
      }, { signal: this.events.signal });
      this.auto = typeof IntersectionObserver !== 'undefined';
      this.link.hidden = this.auto || !this.dataset.next;
      this.classList.add('is-enhanced');
      if (this.auto) {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting) && !this.failed) this.load();
        }, { rootMargin: '600px 0px' });
        if (this.dataset.next) this.observer.observe(this);
      }
    }

    pause() {
      this.paused = true;
      this.request?.abort();
      this.observer?.disconnect();
    }

    disconnectedCallback() {
      this.pause();
      this.events?.abort();
    }

    pageURL(value, after) {
      const url = new URL(value, location.href);
      const page = Number(url.searchParams.get('page'));
      if (url.origin !== location.origin || url.pathname !== new URL(location.href).pathname ||
          !Number.isInteger(page) || page <= after) throw new Error('Invalid collection page');
      return url;
    }

    async load() {
      if (this.loading || this.paused || !this.isConnected || !this.dataset.next) return;
      this.loading = true;
      this.failed = false;
      this.observer?.unobserve(this);
      const request = this.request = new AbortController();
      this.setAttribute('aria-busy', 'true');
      this.status.textContent = 'Loading more products…';
      this.link.hidden = true;
      try {
        const url = this.pageURL(this.dataset.next, Number(this.dataset.page));
        url.searchParams.set('section_id', this.dataset.sectionId);
        const response = await fetch(url.toString(), { signal: request.signal });
        if (!response.ok) throw new Error(response.status);
        const html = await response.text();
        if (request.signal.aborted || this.paused || !this.isConnected) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const grid = parsed.querySelector('#product-grid');
        const paging = parsed.querySelector('primo-collection-scroll');
        const page = Number(paging?.dataset.page);
        if (grid?.dataset.id !== this.dataset.sectionId || page !== Number(url.searchParams.get('page'))) {
          throw new Error('Unexpected collection response');
        }
        const next = paging.dataset.next || '';
        if (next) this.pageURL(next, page);
        const ids = new Set(Array.from(this.grid.children, item => item.dataset.productId));
        let added = 0;
        for (const item of Array.from(grid.children)) {
          const id = item.dataset.productId;
          if (!id || ids.has(id)) continue;
          ids.add(id);
          const card = document.importNode(item, true);
          card.classList.add('scroll-trigger--cancel');
          card.querySelectorAll('img').forEach(image => { image.loading = 'lazy'; });
          this.grid.append(card);
          added++;
        }
        this.dataset.page = String(page);
        this.dataset.next = next;
        this.link.href = next || '#';
        this.link.textContent = 'Load more products';
        this.link.hidden = this.auto || !next;
        this.status.textContent = added ? `${added} more products loaded.` : '';
      } catch (error) {
        if (request.signal.aborted || this.paused || !this.isConnected) return;
        this.failed = true;
        this.status.textContent = 'Couldn’t load more products.';
        this.link.textContent = 'Try again';
        this.link.hidden = false;
      } finally {
        this.loading = false;
        this.setAttribute('aria-busy', 'false');
        if (!this.paused && this.isConnected && !this.failed && this.dataset.next) this.observer?.observe(this);
      }
    }
  });
}
